import { jsx } from "react/jsx-runtime";
import assert from "assert";
import { forwardRef, Children, useContext } from "react";
import { Bleed } from "../Bleed/Bleed.mjs";
import { Box } from "../Box/Box.mjs";
import { HeadingContext } from "../Heading/HeadingContext.mjs";
import { Text } from "../Text/Text.mjs";
import { TextContext } from "../Text/TextContext.mjs";
import { buildDataAttributes } from "../private/buildDataAttributes.mjs";
import { DefaultTextPropsProvider } from "../private/defaultTextProps.mjs";
import { useDefaultBadgeProps } from "./defaultBadgeProps.mjs";
import { inline, verticalPadding } from "./Badge.css.mjs";
const validBadgeTones = [
  "promote",
  "info",
  "neutral",
  "positive",
  "caution",
  "critical"
];
const lightModeBackgroundForTone = {
  positive: "positiveLight",
  critical: "criticalLight",
  info: "infoLight",
  promote: "promoteLight",
  neutral: "neutralLight",
  caution: "cautionLight"
};
const stringifyChildren = (children) => {
  if (typeof children === "string") {
    return children;
  }
  if (typeof children === "number") {
    return children.toString();
  }
  return children.join("");
};
const Badge = forwardRef(
  ({
    tone = "info",
    weight = "regular",
    bleedY: bleedYProp,
    title,
    children,
    id,
    data,
    tabIndex,
    "aria-describedby": ariaDescribedBy,
    ...restProps
  }, ref) => {
    assert(
      validBadgeTones.indexOf(tone) >= 0,
      `Badge tone of "${tone}" is not valid.`
    );
    assert(
      Children.toArray(children).every(
        (child) => ["string", "number"].includes(typeof child)
      ),
      "Badge may only contain strings or numbers"
    );
    const textContext = useContext(TextContext);
    const headingContext = useContext(HeadingContext);
    const isInline = Boolean(textContext || headingContext);
    assert(
      !isInline || isInline && typeof bleedYProp === "undefined",
      "A `Badge` cannot use `bleedY` when rendered inside a `Text` or `Heading` component."
    );
    const { bleedY } = useDefaultBadgeProps({ bleedY: bleedYProp });
    const content = (
      // Ensures the foreground text tone follows the default
      // for the selected background colour
      /* @__PURE__ */ jsx(DefaultTextPropsProvider, { tone: "neutral", children: /* @__PURE__ */ jsx(TextContext.Provider, { value: null, children: /* @__PURE__ */ jsx(
        Box,
        {
          component: "span",
          id,
          ref,
          tabIndex,
          "aria-describedby": ariaDescribedBy,
          title: title ?? (!ariaDescribedBy ? stringifyChildren(children) : void 0),
          background: weight === "strong" ? tone : lightModeBackgroundForTone[tone],
          paddingY: verticalPadding,
          paddingX: "xsmall",
          borderRadius: "standard",
          cursor: "default",
          maxWidth: "content",
          display: !isInline ? "flex" : void 0,
          className: isInline ? inline : void 0,
          ...buildDataAttributes({ data, validateRestProps: restProps }),
          children: /* @__PURE__ */ jsx(Text, { size: "xsmall", weight: "medium", maxLines: 1, children })
        }
      ) }) })
    );
    return bleedY && !isInline ? /* @__PURE__ */ jsx(Bleed, { component: "span", vertical: verticalPadding, children: content }) : content;
  }
);
Badge.displayName = "Badge";
Badge.__isBadge__ = true;
export {
  Badge
};
